// 紫微斗數計算核心算法
export interface BirthInfo {
  year: number;
  month: number;
  day: number;
  hour?: number;
  minute?: number;
  gender: 'male' | 'female';
  isLunar: boolean;
  hasTime: boolean;
}

export interface Star {
  name: string;
  position: number; // 1-12 宮位
  brightness: '廟' | '旺' | '得' | '利' | '平' | '不' | '陷';
  type: 'main' | 'auxiliary' | 'four_stars' | 'minor';
}

export interface Palace {
  name: string;
  position: number;
  stars: Star[];
  earthlyBranch: string;
  heavenlyStem: string;
}

export interface ZiweiChart {
  palaces: Palace[];
  mainStars: Star[];
  birthInfo: BirthInfo;
  lifeStage: string;
  bodyPalace: number;
}

// 天干地支數據
const HEAVENLY_STEMS = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'];
const EARTHLY_BRANCHES = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];

// 十二宮名稱
const PALACE_NAMES = [
  '命宮', '兄弟宮', '夫妻宮', '子女宮', '財帛宮', '疾厄宮',
  '遷移宮', '奴僕宮', '官祿宮', '田宅宮', '福德宮', '父母宮'
];

// 主星數據
const MAIN_STARS = [
  { name: '紫微', type: 'main' as const },
  { name: '天機', type: 'main' as const },
  { name: '太陽', type: 'main' as const },
  { name: '武曲', type: 'main' as const },
  { name: '天同', type: 'main' as const },
  { name: '廉貞', type: 'main' as const },
  { name: '天府', type: 'main' as const },
  { name: '太陰', type: 'main' as const },
  { name: '貪狼', type: 'main' as const },
  { name: '巨門', type: 'main' as const },
  { name: '天相', type: 'main' as const },
  { name: '天梁', type: 'main' as const },
  { name: '七殺', type: 'main' as const },
  { name: '破軍', type: 'main' as const }
];

// 輔星數據
const AUXILIARY_STARS = [
  { name: '左輔', type: 'auxiliary' as const },
  { name: '右弼', type: 'auxiliary' as const },
  { name: '文昌', type: 'auxiliary' as const },
  { name: '文曲', type: 'auxiliary' as const },
  { name: '祿存', type: 'auxiliary' as const },
  { name: '天馬', type: 'auxiliary' as const }
];

// 四化星
const FOUR_STARS = [
  { name: '化祿', type: 'four_stars' as const },
  { name: '化權', type: 'four_stars' as const },
  { name: '化科', type: 'four_stars' as const },
  { name: '化忌', type: 'four_stars' as const }
];

// 計算農曆轉公曆的簡化函數（實際應用中需要更精確的算法）
function lunarToSolar(year: number, month: number, day: number): { year: number; month: number; day: number } {
  // 這裡使用簡化算法，實際應用中需要使用專業的農曆轉換庫
  // 暫時返回原日期，實際項目中應該集成專業的農曆轉換算法
  return { year, month, day };
}

// 計算命宮位置
function calculateLifePalace(birthInfo: BirthInfo): number {
  const { month, day, hour = 12 } = birthInfo;
  
  // 簡化的命宮計算公式
  // 實際公式：寅宮起正月，順數至生月，再從生月宮逆數至生時
  let lifePalace = (month + 1) % 12; // 寅宮起正月
  lifePalace = (lifePalace - Math.floor(hour / 2) + 12) % 12;
  
  return lifePalace === 0 ? 12 : lifePalace;
}

// 計算身宮位置
function calculateBodyPalace(birthInfo: BirthInfo): number {
  const { month, day, hour = 12 } = birthInfo;
  
  // 身宮計算公式
  let bodyPalace = (month + Math.floor(hour / 2)) % 12;
  
  return bodyPalace === 0 ? 12 : bodyPalace;
}

// 計算紫微星位置
function calculateZiweiPosition(birthInfo: BirthInfo): number {
  const { year, month, day } = birthInfo;
  
  // 簡化的紫微星計算
  // 實際計算需要根據出生年月日的天干地支
  const totalDays = year * 365 + month * 30 + day;
  return (totalDays % 12) + 1;
}

// 計算星曜亮度
function calculateBrightness(starName: string, position: number, birthInfo: BirthInfo): Star['brightness'] {
  // 簡化的亮度計算，實際需要根據複雜的規則
  const brightnessList: Star['brightness'][] = ['廟', '旺', '得', '利', '平', '不', '陷'];
  const index = (starName.charCodeAt(0) + position + birthInfo.month) % brightnessList.length;
  return brightnessList[index];
}

// 安排主星
function arrangeMainStars(lifePalacePosition: number, birthInfo: BirthInfo): Star[] {
  const stars: Star[] = [];
  const ziweiPosition = calculateZiweiPosition(birthInfo);
  
  // 紫微星系的安排（簡化版本）
  const starPositions = [
    { name: '紫微', offset: 0 },
    { name: '天機', offset: 1 },
    { name: '太陽', offset: 2 },
    { name: '武曲', offset: 3 },
    { name: '天同', offset: 4 },
    { name: '廉貞', offset: 5 },
    { name: '天府', offset: 6 },
    { name: '太陰', offset: 7 },
    { name: '貪狼', offset: 8 },
    { name: '巨門', offset: 9 },
    { name: '天相', offset: 10 },
    { name: '天梁', offset: 11 },
    { name: '七殺', offset: 0 },
    { name: '破軍', offset: 4 }
  ];
  
  starPositions.forEach(({ name, offset }) => {
    const position = ((ziweiPosition + offset - 1) % 12) + 1;
    stars.push({
      name,
      position,
      brightness: calculateBrightness(name, position, birthInfo),
      type: 'main'
    });
  });
  
  return stars;
}

// 安排輔星
function arrangeAuxiliaryStars(lifePalacePosition: number, birthInfo: BirthInfo): Star[] {
  const stars: Star[] = [];
  
  // 左輔右弼的安排
  stars.push({
    name: '左輔',
    position: ((lifePalacePosition + birthInfo.month - 1) % 12) + 1,
    brightness: '利',
    type: 'auxiliary'
  });
  
  stars.push({
    name: '右弼',
    position: ((lifePalacePosition - birthInfo.month + 11) % 12) + 1,
    brightness: '利',
    type: 'auxiliary'
  });
  
  // 文昌文曲的安排
  const hour = birthInfo.hour || 12;
  stars.push({
    name: '文昌',
    position: ((10 - Math.floor(hour / 2) + 12) % 12) + 1,
    brightness: '旺',
    type: 'auxiliary'
  });
  
  stars.push({
    name: '文曲',
    position: ((4 + Math.floor(hour / 2)) % 12) + 1,
    brightness: '旺',
    type: 'auxiliary'
  });
  
  return stars;
}

// 計算四化星
function calculateFourStars(birthInfo: BirthInfo): Star[] {
  const stars: Star[] = [];
  const yearStem = HEAVENLY_STEMS[(birthInfo.year - 4) % 10];
  
  // 簡化的四化計算
  const fourStarPositions = [
    { name: '化祿', position: (birthInfo.year % 12) + 1 },
    { name: '化權', position: ((birthInfo.year + 1) % 12) + 1 },
    { name: '化科', position: ((birthInfo.year + 2) % 12) + 1 },
    { name: '化忌', position: ((birthInfo.year + 3) % 12) + 1 }
  ];
  
  fourStarPositions.forEach(({ name, position }) => {
    stars.push({
      name,
      position,
      brightness: '平',
      type: 'four_stars'
    });
  });
  
  return stars;
}

// 主要計算函數
export function calculateZiweiChart(birthInfo: BirthInfo): ZiweiChart {
  // 如果是農曆，轉換為公曆
  let adjustedBirthInfo = birthInfo;
  if (birthInfo.isLunar) {
    const solarDate = lunarToSolar(birthInfo.year, birthInfo.month, birthInfo.day);
    adjustedBirthInfo = { ...birthInfo, ...solarDate };
  }
  
  // 如果沒有時間，使用午時（12點）
  if (!birthInfo.hasTime) {
    adjustedBirthInfo = { ...adjustedBirthInfo, hour: 12, minute: 0 };
  }
  
  const lifePalacePosition = calculateLifePalace(adjustedBirthInfo);
  const bodyPalacePosition = calculateBodyPalace(adjustedBirthInfo);
  
  // 安排各種星曜
  const mainStars = arrangeMainStars(lifePalacePosition, adjustedBirthInfo);
  const auxiliaryStars = arrangeAuxiliaryStars(lifePalacePosition, adjustedBirthInfo);
  const fourStars = calculateFourStars(adjustedBirthInfo);
  
  const allStars = [...mainStars, ...auxiliaryStars, ...fourStars];
  
  // 創建十二宮
  const palaces: Palace[] = [];
  for (let i = 1; i <= 12; i++) {
    const palaceStars = allStars.filter(star => star.position === i);
    const branchIndex = (lifePalacePosition + i - 2) % 12;
    const stemIndex = (adjustedBirthInfo.year - 4 + i - 1) % 10;
    
    palaces.push({
      name: PALACE_NAMES[i - 1],
      position: i,
      stars: palaceStars,
      earthlyBranch: EARTHLY_BRANCHES[branchIndex],
      heavenlyStem: HEAVENLY_STEMS[stemIndex]
    });
  }
  
  // 計算大限
  const currentYear = new Date().getFullYear();
  const age = currentYear - adjustedBirthInfo.year;
  const lifeStage = `第${Math.floor(age / 10) + 1}大限 (${age}歲)`;
  
  return {
    palaces,
    mainStars: allStars,
    birthInfo: adjustedBirthInfo,
    lifeStage,
    bodyPalace: bodyPalacePosition
  };
}

// 獲取宮位解讀
export function getPalaceInterpretation(palace: Palace): string {
  const mainStar = palace.stars.find(star => star.type === 'main');
  
  if (!mainStar) {
    return `${palace.name}無主星，需參考對宮星曜或三方四正來判斷。`;
  }
  
  const interpretations: Record<string, Record<string, string>> = {
    '命宮': {
      '紫微': '紫微在命宮，天生具有領導才能，性格高貴，有帝王之相。',
      '天機': '天機在命宮，聰明機智，善於思考，適合從事智慧型工作。',
      '太陽': '太陽在命宮，性格開朗，有正義感，喜歡幫助他人。',
      '武曲': '武曲在命宮，意志堅強，有財運，適合從事金融或軍警工作。',
      '天同': '天同在命宮，性格溫和，人緣佳，生活較為安逸。'
    },
    '財帛宮': {
      '紫微': '紫微在財帛宮，財運亨通，有機會成為富豪。',
      '武曲': '武曲在財帛宮，財運極佳，善於理財投資。',
      '天府': '天府在財帛宮，財庫豐厚，財運穩定。'
    },
    '事業宮': {
      '紫微': '紫微在事業宮，適合從事管理或領導工作。',
      '天機': '天機在事業宮，適合從事策劃、設計等智慧型工作。',
      '武曲': '武曲在事業宮，適合從事金融、軍警等工作。'
    }
  };
  
  const palaceInterpretations = interpretations[palace.name];
  if (palaceInterpretations && palaceInterpretations[mainStar.name]) {
    return palaceInterpretations[mainStar.name];
  }
  
  return `${mainStar.name}在${palace.name}，${mainStar.brightness}地，需要綜合其他因素來判斷。`;
}

// 獲取整體命格解讀
export function getOverallInterpretation(chart: ZiweiChart): string {
  const lifePalace = chart.palaces.find(p => p.name === '命宮');
  const wealthPalace = chart.palaces.find(p => p.name === '財帛宮');
  const careerPalace = chart.palaces.find(p => p.name === '官祿宮');
  
  let interpretation = '整體命格分析：\n\n';
  
  if (lifePalace) {
    interpretation += `命宮：${getPalaceInterpretation(lifePalace)}\n\n`;
  }
  
  if (wealthPalace) {
    interpretation += `財運：${getPalaceInterpretation(wealthPalace)}\n\n`;
  }
  
  if (careerPalace) {
    interpretation += `事業：${getPalaceInterpretation(careerPalace)}\n\n`;
  }
  
  // 添加時辰建議
  if (!chart.birthInfo.hasTime) {
    interpretation += '注意：由於未提供確切出生時間，此排盤使用午時（12:00）計算，可能與實際情況有所差異。建議提供準確出生時間以獲得更精確的分析。';
  }
  
  return interpretation;
}

// 詳細運程分析接口
export interface DetailedFortune {
  aspect: string;
  palace: string;
  stars: string[];
  score: number; // 1-10分
  trend: 'excellent' | 'good' | 'average' | 'poor' | 'challenging';
  description: string;
  suggestions: string[];
  keyPeriods: string[];
}

// 流年運程接口
export interface YearlyFortune {
  year: number;
  age: number;
  overallScore: number;
  aspects: {
    career: number;
    wealth: number;
    health: number;
    relationship: number;
    family: number;
    study: number;
  };
  keyEvents: string[];
  luckyMonths: number[];
  challenges: string[];
  opportunities: string[];
}

// 獲取詳細運程分析
export function getDetailedFortune(chart: ZiweiChart): DetailedFortune[] {
  const fortunes: DetailedFortune[] = [];
  
  // 性格分析（命宮）
  const lifePalace = chart.palaces.find(p => p.name === '命宮');
  if (lifePalace) {
    fortunes.push(analyzePersonality(lifePalace, chart));
  }
  
  // 事業運程（官祿宮）
  const careerPalace = chart.palaces.find(p => p.name === '官祿宮');
  if (careerPalace) {
    fortunes.push(analyzeCareer(careerPalace, chart));
  }
  
  // 財運分析（財帛宮）
  const wealthPalace = chart.palaces.find(p => p.name === '財帛宮');
  if (wealthPalace) {
    fortunes.push(analyzeWealth(wealthPalace, chart));
  }
  
  // 感情運程（夫妻宮）
  const marriagePalace = chart.palaces.find(p => p.name === '夫妻宮');
  if (marriagePalace) {
    fortunes.push(analyzeRelationship(marriagePalace, chart));
  }
  
  // 健康運程（疾厄宮）
  const healthPalace = chart.palaces.find(p => p.name === '疾厄宮');
  if (healthPalace) {
    fortunes.push(analyzeHealth(healthPalace, chart));
  }
  
  // 家宅運程（田宅宮）
  const propertyPalace = chart.palaces.find(p => p.name === '田宅宮');
  if (propertyPalace) {
    fortunes.push(analyzeProperty(propertyPalace, chart));
  }
  
  // 學業運程（官祿宮 + 文昌文曲）
  fortunes.push(analyzeStudy(chart));
  
  // 親情運程（父母宮 + 兄弟宮）
  const parentsPalace = chart.palaces.find(p => p.name === '父母宮');
  const siblingsPalace = chart.palaces.find(p => p.name === '兄弟宮');
  if (parentsPalace && siblingsPalace) {
    fortunes.push(analyzeFamily(parentsPalace, siblingsPalace, chart));
  }
  
  return fortunes;
}

// 性格分析
function analyzePersonality(lifePalace: Palace, chart: ZiweiChart): DetailedFortune {
  const mainStar = lifePalace.stars.find(star => star.type === 'main');
  const starNames = lifePalace.stars.map(s => s.name);
  
  let score = 7;
  let trend: DetailedFortune['trend'] = 'average';
  let description = '';
  let suggestions: string[] = [];
  
  if (mainStar) {
    switch (mainStar.name) {
      case '紫微':
        score = 9;
        trend = 'excellent';
        description = '天生具有領導氣質，性格高貴，有帝王之相。做事有條理，責任感強，但有時過於自信。';
        suggestions = ['發揮領導才能', '注意謙遜待人', '適合管理職位'];
        break;
      case '天機':
        score = 8;
        trend = 'good';
        description = '聰明機智，思維敏捷，善於策劃。好奇心強，學習能力佳，但有時想法變化快。';
        suggestions = ['發揮智慧優勢', '保持專注力', '適合智慧型工作'];
        break;
      case '太陽':
        score = 8;
        trend = 'good';
        description = '性格開朗，正義感強，樂於助人。有領導能力，但有時過於直接。';
        suggestions = ['發揮正面影響力', '注意說話技巧', '適合公職或教育'];
        break;
      case '武曲':
        score = 7;
        trend = 'good';
        description = '意志堅強，執行力佳，有財運天賦。性格剛毅，但有時過於嚴肅。';
        suggestions = ['發揮執行優勢', '培養人際關係', '適合金融或軍警'];
        break;
      case '天同':
        score = 8;
        trend = 'good';
        description = '性格溫和，人緣佳，生活態度樂觀。享受生活，但有時缺乏積極性。';
        suggestions = ['保持樂觀態度', '增強進取心', '適合服務業'];
        break;
      default:
        description = `${mainStar.name}在命宮，${mainStar.brightness}地，性格特質需綜合其他因素判斷。`;
        suggestions = ['發揮星曜優勢', '注意個人修養', '培養全面能力'];
    }
    
    // 根據亮度調整分數
    if (['廟', '旺'].includes(mainStar.brightness)) {
      score = Math.min(10, score + 1);
      trend = score >= 9 ? 'excellent' : 'good';
    } else if (['不', '陷'].includes(mainStar.brightness)) {
      score = Math.max(1, score - 2);
      trend = score <= 4 ? 'challenging' : 'average';
    }
  } else {
    description = '命宮無主星，性格較為溫和，容易受環境影響，需參考對宮星曜。';
    suggestions = ['培養獨立性', '加強自信心', '多學習他人優點'];
    score = 6;
  }
  
  return {
    aspect: '性格特質',
    palace: '命宮',
    stars: starNames,
    score,
    trend,
    description,
    suggestions,
    keyPeriods: ['青年期(16-35歲)', '中年期(36-55歲)']
  };
}

// 事業分析
function analyzeCareer(careerPalace: Palace, chart: ZiweiChart): DetailedFortune {
  const mainStar = careerPalace.stars.find(star => star.type === 'main');
  const starNames = careerPalace.stars.map(s => s.name);
  
  let score = 7;
  let trend: DetailedFortune['trend'] = 'average';
  let description = '';
  let suggestions: string[] = [];
  
  if (mainStar) {
    switch (mainStar.name) {
      case '紫微':
        score = 9;
        trend = 'excellent';
        description = '事業運極佳，適合擔任領導職務。有機會在政府機關或大企業發展，容易獲得上司賞識。';
        suggestions = ['爭取管理職位', '發展領導技能', '建立人脈網絡'];
        break;
      case '天機':
        score = 8;
        trend = 'good';
        description = '適合從事策劃、設計、諮詢等智慧型工作。事業發展穩定，但需要耐心經營。';
        suggestions = ['發揮創意思維', '持續學習新技能', '注重專業發展'];
        break;
      case '武曲':
        score = 8;
        trend = 'good';
        description = '財經、金融、軍警等領域有優勢。事業發展務實穩健，財運與事業相輔相成。';
        suggestions = ['專注財經領域', '建立專業信譽', '把握投資機會'];
        break;
      case '太陽':
        score = 7;
        trend = 'good';
        description = '適合公職、教育、傳媒等行業。事業有起伏，但整體向上發展。';
        suggestions = ['選擇正當行業', '發揮影響力', '注意工作壓力'];
        break;
      default:
        description = `${mainStar.name}在事業宮，事業發展需要努力經營，適合的行業需綜合判斷。`;
        suggestions = ['努力提升能力', '尋找適合領域', '保持積極態度'];
    }
    
    // 根據亮度調整
    if (['廟', '旺'].includes(mainStar.brightness)) {
      score = Math.min(10, score + 1);
      trend = score >= 9 ? 'excellent' : 'good';
    } else if (['不', '陷'].includes(mainStar.brightness)) {
      score = Math.max(1, score - 2);
      trend = score <= 4 ? 'challenging' : 'average';
    }
  } else {
    description = '事業宮無主星，事業發展較為平穩，需要自己努力開創。';
    suggestions = ['積極開創機會', '培養專業技能', '建立良好人際關係'];
    score = 6;
  }
  
  return {
    aspect: '事業運程',
    palace: '官祿宮',
    stars: starNames,
    score,
    trend,
    description,
    suggestions,
    keyPeriods: ['30-40歲', '45-55歲']
  };
}

// 財運分析
function analyzeWealth(wealthPalace: Palace, chart: ZiweiChart): DetailedFortune {
  const mainStar = wealthPalace.stars.find(star => star.type === 'main');
  const starNames = wealthPalace.stars.map(s => s.name);
  
  let score = 7;
  let trend: DetailedFortune['trend'] = 'average';
  let description = '';
  let suggestions: string[] = [];
  
  if (mainStar) {
    switch (mainStar.name) {
      case '武曲':
        score = 9;
        trend = 'excellent';
        description = '財運極佳，有理財天賦。適合投資理財，容易累積財富。正財偏財皆有機會。';
        suggestions = ['學習投資理財', '建立多元收入', '謹慎理財規劃'];
        break;
      case '天府':
        score = 8;
        trend = 'good';
        description = '財庫豐厚，財運穩定。善於儲蓄，財富累積穩健，但增長較慢。';
        suggestions = ['穩健投資', '建立儲蓄習慣', '避免高風險投資'];
        break;
      case '紫微':
        score = 8;
        trend = 'good';
        description = '財運亨通，有機會因地位提升而增加收入。適合從事高端行業。';
        suggestions = ['提升個人地位', '發展高端客戶', '注重品牌形象'];
        break;
      case '貪狼':
        score = 7;
        trend = 'average';
        description = '財運有起伏，偏財運較強。需要控制消費慾望，避免投機。';
        suggestions = ['控制消費慾望', '避免投機行為', '建立理財紀律'];
        break;
      default:
        description = `${mainStar.name}在財帛宮，財運需要努力經營，理財方式需謹慎選擇。`;
        suggestions = ['努力增加收入', '學習理財知識', '建立財務目標'];
    }
    
    // 根據亮度調整
    if (['廟', '旺'].includes(mainStar.brightness)) {
      score = Math.min(10, score + 1);
      trend = score >= 9 ? 'excellent' : 'good';
    } else if (['不', '陷'].includes(mainStar.brightness)) {
      score = Math.max(1, score - 2);
      trend = score <= 4 ? 'challenging' : 'average';
    }
  } else {
    description = '財帛宮無主星，財運平穩，需要勤儉持家，穩健理財。';
    suggestions = ['勤儉節約', '穩健投資', '建立應急基金'];
    score = 6;
  }
  
  return {
    aspect: '財運分析',
    palace: '財帛宮',
    stars: starNames,
    score,
    trend,
    description,
    suggestions,
    keyPeriods: ['25-35歲', '40-50歲']
  };
}

// 感情分析
function analyzeRelationship(marriagePalace: Palace, chart: ZiweiChart): DetailedFortune {
  const mainStar = marriagePalace.stars.find(star => star.type === 'main');
  const starNames = marriagePalace.stars.map(s => s.name);
  
  let score = 7;
  let trend: DetailedFortune['trend'] = 'average';
  let description = '';
  let suggestions: string[] = [];
  
  if (mainStar) {
    switch (mainStar.name) {
      case '天同':
        score = 9;
        trend = 'excellent';
        description = '感情運佳，容易遇到合適對象。婚姻生活和諧，夫妻感情深厚。';
        suggestions = ['珍惜感情', '保持溝通', '共同成長'];
        break;
      case '太陰':
        score = 8;
        trend = 'good';
        description = '感情細膩，對愛情有美好憧憬。適合與溫柔體貼的對象交往。';
        suggestions = ['培養浪漫情懷', '學會表達情感', '注重精神交流'];
        break;
      case '天相':
        score = 8;
        trend = 'good';
        description = '感情穩定，重視家庭和諧。婚後生活美滿，是好的伴侶。';
        suggestions = ['重視家庭責任', '維護感情穩定', '建立共同目標'];
        break;
      case '廉貞':
        score = 6;
        trend = 'average';
        description = '感情較為複雜，容易有波折。需要學會處理感情問題。';
        suggestions = ['學會溝通技巧', '控制情緒波動', '建立信任關係'];
        break;
      case '破軍':
        score = 5;
        trend = 'challenging';
        description = '感情變化較大，容易有分分合合。需要更多耐心經營感情。';
        suggestions = ['培養耐心', '學會妥協', '避免衝動決定'];
        break;
      default:
        description = `${mainStar.name}在夫妻宮，感情發展需要用心經營，適合的對象類型需綜合判斷。`;
        suggestions = ['用心經營感情', '了解對方需求', '建立良好溝通'];
    }
    
    // 根據亮度調整
    if (['廟', '旺'].includes(mainStar.brightness)) {
      score = Math.min(10, score + 1);
      trend = score >= 9 ? 'excellent' : 'good';
    } else if (['不', '陷'].includes(mainStar.brightness)) {
      score = Math.max(1, score - 2);
      trend = score <= 4 ? 'challenging' : 'average';
    }
  } else {
    description = '夫妻宮無主星，感情發展較為平淡，需要主動經營。';
    suggestions = ['主動尋找機會', '培養個人魅力', '學習相處技巧'];
    score = 6;
  }
  
  return {
    aspect: '感情運程',
    palace: '夫妻宮',
    stars: starNames,
    score,
    trend,
    description,
    suggestions,
    keyPeriods: ['20-30歲', '35-45歲']
  };
}

// 健康分析
function analyzeHealth(healthPalace: Palace, chart: ZiweiChart): DetailedFortune {
  const mainStar = healthPalace.stars.find(star => star.type === 'main');
  const starNames = healthPalace.stars.map(s => s.name);
  
  let score = 7;
  let trend: DetailedFortune['trend'] = 'average';
  let description = '';
  let suggestions: string[] = [];
  
  if (mainStar) {
    switch (mainStar.name) {
      case '天同':
        score = 8;
        trend = 'good';
        description = '體質較好，很少生病。注意不要過度安逸，需要適當運動。';
        suggestions = ['保持運動習慣', '注意飲食均衡', '避免過度安逸'];
        break;
      case '太陽':
        score = 7;
        trend = 'good';
        description = '精力充沛，但容易因工作過度而疲勞。注意眼部和心臟健康。';
        suggestions = ['注意工作休息', '保護眼部健康', '定期健康檢查'];
        break;
      case '太陰':
        score = 6;
        trend = 'average';
        description = '體質偏弱，容易受情緒影響。注意婦科或內分泌問題。';
        suggestions = ['調節情緒', '注意內分泌', '加強體質鍛煉'];
        break;
      case '武曲':
        score = 7;
        trend = 'average';
        description = '體質堅強，但容易有外傷或呼吸系統問題。';
        suggestions = ['注意安全防護', '保護呼吸系統', '避免危險活動'];
        break;
      case '廉貞':
        score = 5;
        trend = 'challenging';
        description = '容易有血液循環或皮膚問題。需要注意情緒管理。';
        suggestions = ['注意血液循環', '保護皮膚健康', '學會情緒管理'];
        break;
      default:
        description = `${mainStar.name}在疾厄宮，健康狀況需要注意，預防勝於治療。`;
        suggestions = ['定期健康檢查', '保持良好作息', '注意飲食健康'];
    }
    
    // 根據亮度調整
    if (['廟', '旺'].includes(mainStar.brightness)) {
      score = Math.min(10, score + 1);
      trend = score >= 8 ? 'good' : 'average';
    } else if (['不', '陷'].includes(mainStar.brightness)) {
      score = Math.max(1, score - 2);
      trend = score <= 4 ? 'challenging' : 'poor';
    }
  } else {
    description = '疾厄宮無主星，健康狀況較為穩定，但需要注意保養。';
    suggestions = ['保持健康生活', '定期體檢', '預防疾病'];
    score = 7;
  }
  
  return {
    aspect: '健康運程',
    palace: '疾厄宮',
    stars: starNames,
    score,
    trend,
    description,
    suggestions,
    keyPeriods: ['中年期', '老年期']
  };
}

// 家宅分析
function analyzeProperty(propertyPalace: Palace, chart: ZiweiChart): DetailedFortune {
  const mainStar = propertyPalace.stars.find(star => star.type === 'main');
  const starNames = propertyPalace.stars.map(s => s.name);
  
  let score = 7;
  let trend: DetailedFortune['trend'] = 'average';
  let description = '';
  let suggestions: string[] = [];
  
  if (mainStar) {
    switch (mainStar.name) {
      case '天府':
        score = 9;
        trend = 'excellent';
        description = '家宅運極佳，容易擁有不動產。家庭和睦，居住環境優良。';
        suggestions = ['投資房地產', '改善居住環境', '維護家庭和諧'];
        break;
      case '紫微':
        score = 8;
        trend = 'good';
        description = '家宅豪華，居住品質高。家族地位崇高，但開支較大。';
        suggestions = ['選擇優質住宅', '注意家庭開支', '維護家族聲譽'];
        break;
      case '太陰':
        score = 8;
        trend = 'good';
        description = '家庭溫馨，居住環境舒適。女性在家中地位重要。';
        suggestions = ['營造溫馨氛圍', '重視女性意見', '保持家庭整潔'];
        break;
      case '天同':
        score = 7;
        trend = 'good';
        description = '家庭生活安逸，居住穩定。家人關係和睦，但缺乏進取心。';
        suggestions = ['保持家庭和睦', '適度改善環境', '鼓勵家人進步'];
        break;
      default:
        description = `${mainStar.name}在田宅宮，家宅運勢需要努力經營，居住環境可以改善。`;
        suggestions = ['努力改善居住', '維護家庭關係', '合理規劃空間'];
    }
    
    // 根據亮度調整
    if (['廟', '旺'].includes(mainStar.brightness)) {
      score = Math.min(10, score + 1);
      trend = score >= 9 ? 'excellent' : 'good';
    } else if (['不', '陷'].includes(mainStar.brightness)) {
      score = Math.max(1, score - 2);
      trend = score <= 4 ? 'challenging' : 'average';
    }
  } else {
    description = '田宅宮無主星，家宅運勢平穩，需要自己努力經營。';
    suggestions = ['努力購置房產', '改善居住條件', '維護家庭和諧'];
    score = 6;
  }
  
  return {
    aspect: '家宅運程',
    palace: '田宅宮',
    stars: starNames,
    score,
    trend,
    description,
    suggestions,
    keyPeriods: ['成家立業期', '中年安定期']
  };
}

// 學業分析
function analyzeStudy(chart: ZiweiChart): DetailedFortune {
  const careerPalace = chart.palaces.find(p => p.name === '官祿宮');
  const allStars = chart.palaces.flatMap(p => p.stars);
  const wenchang = allStars.find(s => s.name === '文昌');
  const wenqu = allStars.find(s => s.name === '文曲');
  
  let score = 7;
  let trend: DetailedFortune['trend'] = 'average';
  let description = '';
  let suggestions: string[] = [];
  let starNames: string[] = [];
  
  // 檢查文昌文曲
  if (wenchang) starNames.push(wenchang.name);
  if (wenqu) starNames.push(wenqu.name);
  
  if (wenchang && wenqu) {
    score = 9;
    trend = 'excellent';
    description = '學業運極佳，文昌文曲雙全。考試運佳，適合學術研究或文職工作。';
    suggestions = ['專心學業', '參加考試', '發展文學才能'];
  } else if (wenchang || wenqu) {
    score = 8;
    trend = 'good';
    description = `有${wenchang ? '文昌' : '文曲'}星，學業運不錯。在文學、藝術方面有天賦。`;
    suggestions = ['發揮文學天賦', '加強學習', '培養藝術興趣'];
  } else {
    // 檢查官祿宮主星
    const careerMainStar = careerPalace?.stars.find(star => star.type === 'main');
    if (careerMainStar) {
      starNames.push(careerMainStar.name);
      switch (careerMainStar.name) {
        case '天機':
          score = 8;
          trend = 'good';
          description = '天機星主智慧，學習能力強，適合理工科或策劃類學科。';
          suggestions = ['發揮邏輯思維', '學習理工科目', '培養分析能力'];
          break;
        case '太陽':
          score = 7;
          trend = 'good';
          description = '太陽星主光明，適合法律、政治、教育等學科。';
          suggestions = ['學習社會科學', '培養領導能力', '參與社團活動'];
          break;
        default:
          description = '學業運勢一般，需要努力學習，培養興趣。';
          suggestions = ['努力學習', '培養興趣', '尋找適合方向'];
      }
    } else {
      description = '學業運勢平穩，需要勤奮努力，找到適合的學習方法。';
      suggestions = ['勤奮學習', '找對方法', '培養學習興趣'];
      score = 6;
    }
  }
  
  return {
    aspect: '學業運程',
    palace: '官祿宮 + 文星',
    stars: starNames,
    score,
    trend,
    description,
    suggestions,
    keyPeriods: ['求學期', '進修期']
  };
}

// 親情分析
function analyzeFamily(parentsPalace: Palace, siblingsPalace: Palace, chart: ZiweiChart): DetailedFortune {
  const parentsMainStar = parentsPalace.stars.find(star => star.type === 'main');
  const siblingsMainStar = siblingsPalace.stars.find(star => star.type === 'main');
  const starNames = [...parentsPalace.stars.map(s => s.name), ...siblingsPalace.stars.map(s => s.name)];
  
  let score = 7;
  let trend: DetailedFortune['trend'] = 'average';
  let description = '';
  let suggestions: string[] = [];
  
  // 分析父母宮
  let parentsScore = 7;
  if (parentsMainStar) {
    switch (parentsMainStar.name) {
      case '太陽':
        parentsScore = 8;
        description += '與父親關係較好，父親對你影響深遠。';
        break;
      case '太陰':
        parentsScore = 8;
        description += '與母親關係密切，母親給予很多關愛。';
        break;
      case '天府':
        parentsScore = 9;
        description += '父母地位崇高，家庭背景良好。';
        break;
      case '天同':
        parentsScore = 8;
        description += '父母慈祥，家庭氣氛和諧。';
        break;
      default:
        description += '與父母關係需要用心維護。';
    }
  } else {
    description += '父母緣分較淡，需要主動關心。';
    parentsScore = 6;
  }
  
  // 分析兄弟宮
  let siblingsScore = 7;
  if (siblingsMainStar) {
    switch (siblingsMainStar.name) {
      case '天同':
        siblingsScore = 8;
        description += ' 兄弟姊妹關係和睦，互相幫助。';
        break;
      case '天機':
        siblingsScore = 7;
        description += ' 兄弟姊妹聰明，但關係可能有變化。';
        break;
      case '武曲':
        siblingsScore = 6;
        description += ' 兄弟姊妹個性較強，需要多溝通。';
        break;
      default:
        description += ' 兄弟姊妹關係一般，需要用心經營。';
    }
  } else {
    description += ' 兄弟姊妹緣分較淡。';
    siblingsScore = 6;
  }
  
  score = Math.round((parentsScore + siblingsScore) / 2);
  
  if (score >= 8) {
    trend = 'good';
    suggestions = ['珍惜親情', '常回家看看', '維護家庭和諧'];
  } else if (score >= 6) {
    trend = 'average';
    suggestions = ['主動關心家人', '增進溝通', '化解家庭矛盾'];
  } else {
    trend = 'challenging';
    suggestions = ['努力改善關係', '學會包容', '尋求專業協助'];
  }
  
  return {
    aspect: '親情運程',
    palace: '父母宮 + 兄弟宮',
    stars: starNames,
    score,
    trend,
    description,
    suggestions,
    keyPeriods: ['青少年期', '成年後']
  };
}

// 計算流年運程
export function calculateYearlyFortune(chart: ZiweiChart, targetYear: number): YearlyFortune {
  const currentAge = targetYear - chart.birthInfo.year;
  const yearBranch = EARTHLY_BRANCHES[(targetYear - 4) % 12];
  const yearStem = HEAVENLY_STEMS[(targetYear - 4) % 10];
  
  // 基礎運勢計算
  let overallScore = 7;
  let aspects = {
    career: 7,
    wealth: 7,
    health: 7,
    relationship: 7,
    family: 7,
    study: 7
  };
  
  // 根據流年天干地支調整運勢
  const yearCycle = (targetYear - chart.birthInfo.year) % 12;
  
  // 大限影響
  const majorPeriod = Math.floor(currentAge / 10);
  const majorPalaceIndex = (majorPeriod - 1) % 12;
  const majorPalace = chart.palaces[majorPalaceIndex];
  
  if (majorPalace) {
    const majorStar = majorPalace.stars.find(s => s.type === 'main');
    if (majorStar) {
      // 根據大限主星調整整體運勢
      switch (majorStar.name) {
        case '紫微':
        case '天府':
          overallScore += 1;
          aspects.career += 2;
          aspects.wealth += 1;
          break;
        case '武曲':
          aspects.wealth += 2;
          aspects.career += 1;
          break;
        case '天同':
          aspects.health += 1;
          aspects.family += 1;
          break;
        case '太陽':
          aspects.career += 1;
          aspects.relationship += 1;
          break;
      }
    }
  }
  
  // 流年特殊影響
  const keyEvents: string[] = [];
  const luckyMonths: number[] = [];
  const challenges: string[] = [];
  const opportunities: string[] = [];
  
  // 根據年齡階段調整
  if (currentAge >= 20 && currentAge <= 30) {
    aspects.study += 1;
    aspects.relationship += 1;
    keyEvents.push('學業發展關鍵期', '感情運勢上升');
    opportunities.push('學習新技能', '建立感情關係');
  } else if (currentAge >= 30 && currentAge <= 45) {
    aspects.career += 1;
    aspects.wealth += 1;
    keyEvents.push('事業發展黃金期', '財運逐漸上升');
    opportunities.push('職業晉升', '投資理財');
  } else if (currentAge >= 45 && currentAge <= 60) {
    aspects.family += 1;
    aspects.health -= 1;
    keyEvents.push('家庭責任重大', '注意健康保養');
    challenges.push('工作壓力', '健康問題');
  } else if (currentAge > 60) {
    aspects.health -= 1;
    aspects.family += 1;
    keyEvents.push('享受天倫之樂', '注意身體健康');
    opportunities.push('含飴弄孫', '養生保健');
  }
  
  // 計算幸運月份（簡化算法）
  for (let month = 1; month <= 12; month++) {
    if ((month + yearCycle) % 3 === 0) {
      luckyMonths.push(month);
    }
  }
  
  // 根據流年干支添加特殊事件
  if (yearStem === '甲' || yearStem === '乙') {
    opportunities.push('文書考試運佳');
    aspects.study += 1;
  }
  if (yearBranch === '子' || yearBranch === '午') {
    keyEvents.push('人際關係變化');
  }
  
  // 確保分數在合理範圍內
  overallScore = Math.max(1, Math.min(10, overallScore));
  Object.keys(aspects).forEach(key => {
    aspects[key as keyof typeof aspects] = Math.max(1, Math.min(10, aspects[key as keyof typeof aspects]));
  });
  
  return {
    year: targetYear,
    age: currentAge,
    overallScore,
    aspects,
    keyEvents,
    luckyMonths,
    challenges,
    opportunities
  };
}